import { LoadingPage } from '@common/LoadingPage'
import { identifyUser, markInternalUser, resetAnalytics } from '@/lib/analytics'
import { ensureValidAccessToken } from '@services/api-client'
import { useProfile } from '@services/users/queries/useProfile'
import { queryClient } from '@services/utils'
import { userStorage } from '@services/utils/local-storage'
import { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Token } from 'types/api'
import { AuthenticationContextType, AuthenticationStatus } from '../types'

export const AuthenticationContext = createContext<
  AuthenticationContextType | undefined
>(undefined)

export function AuthenticationProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate()

  const user = userStorage.get()
  const enabled = Boolean(user)
  const { data, isLoading, isError } = useProfile(enabled)

  useEffect(() => {
    if (!user) {
      return
    }

    // Warm the token after a long idle so the first axios call and the
    // non-axios paths (livability fetch, analytics) don't start with a 401.
    // Mid-session expiry is handled by the axios pre-flight interceptor and
    // the 401 retry in api-client.
    void ensureValidAccessToken()
  }, [user?.accessToken])

  useEffect(() => {
    if (!data?.id || !data.role) {
      return
    }

    if (data.role === 'ADMIN') {
      markInternalUser()
    } else {
      identifyUser(data.id, data.role)
    }
  }, [data?.id, data?.role])

  const state = useMemo<AuthenticationContextType>(
    () => ({
      user,
      role: user && data?.role,
      loginSatus:
        user && data?.role
          ? ('loggedIn' as AuthenticationStatus)
          : ('loggedOut' as AuthenticationStatus),
      setUser: function (tokens: Token) {
        userStorage.save(tokens)
        navigate('/')
      },
      logout: () => {
        userStorage.clear()
        resetAnalytics()
        // Drop all cached personal data (profile, chats, …) so nothing —
        // e.g. the blinking "new message" tab title — survives logout.
        queryClient.clear()
        navigate('/login')
      }
    }),
    [user, navigate, data]
  )
  if (isLoading) {
    return <LoadingPage />
  }

  if (isError) {
    return <></>
  }

  return (
    <AuthenticationContext.Provider value={state}>
      {children}
    </AuthenticationContext.Provider>
  )
}

export function useAuthentication(): AuthenticationContextType {
  const context = useContext(AuthenticationContext)

  if (!context) {
    throw new Error('AuthenticationContext is missing')
  }

  return context
}
